import axios from 'axios'
import './axiosConfig' // Ensures interceptors are registered
import { downloadExcelBlob } from './consumerService'
import {
  toGatewayError,
  type GatewayError,
  type GatewayResult,
} from '@/utils/gatewayError'

const API_BASE_URL = import.meta.env.VITE_API_URL

export type DetailedReadingsSource =
  | 'waterCards'
  | 'meterNumbers'
  | 'waterTree'
  | 'usageTypes'

export type DetailedReadingsSelection = {
  source: DetailedReadingsSource
  values: string[]
}

export type DetailedReadingsRequest = {
  accountId: string
  from: string
  to: string
  selection: DetailedReadingsSelection
  sortBy: string
  sortOrder: 'ASC' | 'DESC'
  langKey: string
}

export type DetailedReading = {
  meterNumber: number
  billingId: number | null
  name: string | null
  address: string | null
  readingAt: string
  reading: number
}

export type DetailedReadingRow = DetailedReading & { id: number }

export type DetailedReadingsPage = { rows: DetailedReadingRow[]; total: number }

const LOAD_FAILED = 'טעינת הדוח נכשלה'
const EXPORT_FAILED = 'הורדת הקובץ נכשלה'

const reportUrl = (
  { accountId, from, to, sortBy, sortOrder, langKey }: DetailedReadingsRequest,
  path: string,
  paging: Record<string, string> = {}
) => {
  const query = new URLSearchParams({
    from,
    to,
    ...paging,
    sortBy,
    sortOrder,
    langKey,
  })
  return `${API_BASE_URL}/api/reports/${accountId}/detailed-readings${path}?${query}`
}

// A blob request gets its error body as a blob too.
const readBody = async (data: unknown): Promise<unknown> => {
  if (!(data instanceof Blob)) return data
  try {
    return JSON.parse(await data.text())
  } catch {
    return undefined
  }
}

// Only the Gateway's own words reach the user; a request that never got an
// answer shows the Hebrew fallback instead of axios's English text.
const failureOf = async (
  error: unknown,
  fallback: string
): Promise<GatewayError> => {
  const response = axios.isAxiosError(error) ? error.response : undefined
  return toGatewayError(
    {
      response: response && {
        status: response.status,
        data: await readBody(response.data),
      },
    },
    fallback
  )
}

export const getDetailedReadings = async (
  request: DetailedReadingsRequest,
  { page, size }: { page: number; size: number }
): Promise<GatewayResult<DetailedReadingsPage>> => {
  try {
    const res = await axios.post<{
      data?: { total?: number; result?: DetailedReading[] }
    }>(
      reportUrl(request, '', { page: String(page), size: String(size) }),
      request.selection
    )
    const { total = 0, result = [] } = res.data?.data ?? {}
    return {
      ok: true,
      data: {
        total,
        rows: result.map((reading, index) => ({
          id: (page - 1) * size + index,
          ...reading,
        })),
      },
    }
  } catch (error) {
    return { ok: false, error: await failureOf(error, LOAD_FAILED) }
  }
}

export const exportDetailedReadings = async (
  request: DetailedReadingsRequest
): Promise<GatewayResult<null>> => {
  try {
    await downloadExcelBlob(
      reportUrl(request, '/export'),
      'detailed-readings.xlsx',
      undefined,
      request.selection
    )
    return { ok: true, data: null }
  } catch (error) {
    return { ok: false, error: await failureOf(error, EXPORT_FAILED) }
  }
}
